import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Eye, EyeOff, Lock, GripVertical, RotateCcw } from 'lucide-react';
import { GridColumn } from '../../../types/datagrid';

interface ColumnManagerProps {
  isOpen: boolean;
  onClose: () => void;
  columns: GridColumn[];
  onColumnChange: (columns: GridColumn[]) => void;
  onColumnReorder: (columnId: string, targetIndex: number) => void;
  onColumnVisibilityChange: (columnId: string, visible: boolean) => void;
  onColumnRename: (columnId: string, newLabel: string) => void;
  onReset: () => void;
  permissions?: {
    canEdit?: boolean;
    canCreateView?: boolean;
  };
}

export const ColumnManager: React.FC<ColumnManagerProps> = ({
  isOpen,
  onClose,
  columns,
  onColumnVisibilityChange,
  onColumnRename,
  onReset,
  permissions = {},
}) => {
  const [editingColumn, setEditingColumn] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState('');

  if (!isOpen) return null;

  const handleRename = (column: GridColumn) => {
    if (!column.isRenameable) return;

    setEditingColumn(column.id);
    setEditingLabel(column.label);
  };

  const saveRename = (columnId: string) => {
    onColumnRename(columnId, editingLabel);
    setEditingColumn(null);
    setEditingLabel('');
  };

  const cancelRename = () => {
    setEditingColumn(null);
    setEditingLabel('');
  };

  const visibleColumns = columns.filter(col => col.isVisible);
  const hiddenColumns = columns.filter(col => !col.isVisible);

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90] animate-in fade-in duration-300"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed top-6 right-6 bottom-6 z-[100] w-[60%] max-w-2xl animate-in slide-in-from-right duration-300">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 h-full flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 bg-white rounded-t-2xl flex-shrink-0">
            <div className="flex items-center space-x-3">
              <div className="h-8 w-8 bg-brand-100 rounded-lg flex items-center justify-center">
                <Eye className="h-4 w-4 text-brand-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Manage Columns</h2>
            </div>
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={onReset}
                className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                title="Reset to default"
              >
                <RotateCcw className="h-4 w-4" />
                <span>Reset</span>
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
            {/* Visible Columns */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-3">
                Visible Columns ({visibleColumns.length})
              </h3>
              <div className="space-y-2">
                {visibleColumns
                  .sort((a, b) => a.order - b.order)
                  .map(column => (
                    <div
                      key={column.id}
                      className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg group"
                    >
                      {/* Drag Handle */}
                      <div className="text-gray-400 cursor-grab">
                        <GripVertical className="h-4 w-4" />
                      </div>

                      {/* Visibility Toggle */}
                      <button
                        type="button"
                        onClick={() => onColumnVisibilityChange(column.id, false)}
                        disabled={column.isPrivate}
                        className="text-green-600 hover:text-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={column.isPrivate ? 'Cannot hide private column' : 'Hide column'}
                      >
                        <Eye className="h-4 w-4" />
                      </button>

                      {/* Column Label */}
                      <div className="flex-1 min-w-0">
                        {editingColumn === column.id ? (
                          <div className="flex items-center space-x-2">
                            <input
                              type="text"
                              value={editingLabel}
                              onChange={e => setEditingLabel(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') saveRename(column.id);
                                if (e.key === 'Escape') cancelRename();
                              }}
                              onBlur={() => saveRename(column.id)}
                              className="flex-1 px-2 py-1 text-sm border border-brand-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                              autoFocus
                            />
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <span
                              className={`
                                text-sm font-medium text-gray-900 truncate
                                ${column.isRenameable ? 'cursor-pointer hover:text-brand-600' : ''}
                              `}
                              onClick={() => column.isRenameable && handleRename(column)}
                              title={column.isRenameable ? 'Click to rename' : undefined}
                            >
                              {column.label}
                            </span>
                            {column.isPrivate && (
                              <span title="Private column">
                                <Lock
                                  className="h-3 w-3 text-gray-400"
                                  aria-label="Private column"
                                />
                              </span>
                            )}
                            {column.type === 'calculated' && (
                              <span className="text-xs text-blue-600 font-mono px-1 py-0.5 bg-blue-100 rounded">
                                fx
                              </span>
                            )}
                          </div>
                        )}
                        <div className="text-xs text-gray-500 mt-1">
                          {column.type} • {column.key}
                        </div>
                      </div>

                      {/* Column Type Badge */}
                      <span className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded-full">
                        {column.type}
                      </span>
                    </div>
                  ))}
              </div>
            </div>

            {/* Hidden Columns */}
            {hiddenColumns.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-3">
                  Hidden Columns ({hiddenColumns.length})
                </h3>
                <div className="space-y-2">
                  {hiddenColumns.map(column => (
                    <div
                      key={column.id}
                      className="flex items-center space-x-3 p-3 bg-gray-100 rounded-lg opacity-60"
                    >
                      {/* Show Button */}
                      <button
                        type="button"
                        onClick={() => onColumnVisibilityChange(column.id, true)}
                        className="text-gray-400 hover:text-green-600"
                        title="Show column"
                      >
                        <EyeOff className="h-4 w-4" />
                      </button>

                      {/* Column Label */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-700 truncate">
                            {column.label}
                          </span>
                          {column.type === 'calculated' && (
                            <span className="text-xs text-blue-600 font-mono px-1 py-0.5 bg-blue-100 rounded">
                              fx
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {column.type} • {column.key}
                        </div>
                      </div>

                      {/* Column Type Badge */}
                      <span className="text-xs px-2 py-1 bg-gray-300 text-gray-600 rounded-full">
                        {column.type}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add Calculated Column */}
            {permissions.canCreateView && (
              <div className="pt-4 border-t border-gray-200">
                <button
                  type="button"
                  className="flex items-center space-x-2 px-4 py-2 text-sm text-brand-600 hover:text-brand-700 hover:bg-brand-50 rounded-lg border border-dashed border-brand-300 hover:border-brand-400 transition-colors w-full"
                  onClick={() => {
                    // This would open the calculated column editor
                    console.log('Add calculated column');
                  }}
                >
                  <span className="font-mono text-blue-600">fx</span>
                  <span>Add Calculated Column</span>
                </button>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-8 py-6 bg-gray-50 rounded-b-2xl flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {visibleColumns.length} of {columns.length} columns visible
              </div>
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};
